import { Edit3, ArrowRight, ArrowLeft, Lock, Globe, Pencil } from 'lucide-react'
import { T } from '../theme'
import { countAnnotationSets } from '../utils/annotationSets'

const GOLD  = '#4F8EF7'
const INK   = '#1a1916'
const MUTED = 'rgba(26,25,22,0.45)'
const RULE  = 'rgba(26,25,22,0.09)'
const GREEN = '#16a34a'
const MONO  = T.mono

const TYPE_META = {
  goal:                { label: 'But',                  color: '#4F8EF7' },
  chance:              { label: 'Occasion',             color: '#f59e0b' },
  loss:                { label: 'Perte',                color: '#dc2626' },
  recovery:            { label: 'Recup.',               color: '#16a34a' },
  transition_for:      { label: 'Transition off.',      color: '#3b82f6' },
  transition_against:  { label: 'Transition subie',     color: '#991b1b' },
  defensive_phase:     { label: 'Phase def.',           color: '#6b7280' },
  set_piece:           { label: 'CPA',                  color: '#a855f7' },
  tactical_note:       { label: 'Note',                 color: '#9ca3af' },
  player_action:       { label: 'Geste',                color: '#6366f1' },
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  if (h > 0) return `${String(h).padStart(2, '0')}:${mm}:${ss}`
  return `${mm}:${ss}`
}

/**
 * Une ligne de moment, partagée par les deux colonnes (zéro duplication).
 *
 * @prop variant 'private' (Mes moments) | 'for_card' (Pour les joueurs)
 */
function MomentRow({ moment: m, last, playerById, onSeek, onEdit, variant, onMove, onTogglePublic }) {
  const annotationCount = countAnnotationSets(m)
  const meta = TYPE_META[m.moment_type] || { label: m.moment_type, color: MUTED }
  const playerNames = (m.player_ids || [])
    .map(id => {
      const p = playerById.get(id)
      return p ? (p.number != null ? `#${p.number} ${p.name}` : p.name) : null
    })
    .filter(Boolean)
  const isPrivate = variant === 'for_card' && m.is_public === false

  return (
    <div
      style={{
        padding: '12px 14px',
        borderBottom: last ? 'none' : `1px solid ${RULE}`,
        display: 'flex', alignItems: 'flex-start', gap: 10,
        cursor: 'pointer', transition: 'background .12s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(79,142,247,0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      onClick={() => onSeek && onSeek(m.timecode_start)}
    >
      {/* Flèche dorée : déplace le moment d'une colonne à l'autre */}
      {variant === 'for_card' && (
        <button
          onClick={e => { e.stopPropagation(); onMove && onMove(m) }}
          title="Repasser dans Mes moments"
          style={{
            flexShrink: 0, width: 28, height: 28, padding: 0, cursor: 'pointer',
            background: 'rgba(79,142,247,0.1)', border: `1px solid rgba(79,142,247,0.35)`,
            color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <ArrowLeft size={13} />
        </button>
      )}

      <div style={{ width: 3, alignSelf: 'stretch', minHeight: 36, background: meta.color, flexShrink: 0 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontFamily: MONO, fontSize: 11, color: INK, fontWeight: 700 }}>
            {formatTime(m.timecode_start)}
          </span>
          <span style={{
            fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '2px 7px', background: `${meta.color}15`, color: meta.color,
            border: `1px solid ${meta.color}40`,
          }}>
            {meta.label}
          </span>
          {annotationCount > 0 && (
            <span
              title={`${annotationCount} frame${annotationCount > 1 ? 's' : ''} annotée${annotationCount > 1 ? 's' : ''}`}
              style={{
                fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
                padding: '2px 7px', background: T.goldBg2, color: T.gold,
                border: `1px solid ${T.goldBdr}`,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              <Pencil size={9} /> {annotationCount}
            </span>
          )}
          {isPrivate && (
            <span style={{
              fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
              padding: '2px 7px', color: MUTED, border: `1px solid ${RULE}`,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <Lock size={9} /> Privé
            </span>
          )}
        </div>

        {playerNames.length > 0 && (
          <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginBottom: 4 }}>
            {playerNames.join(' · ')}
          </div>
        )}

        {m.note && (
          <div style={{
            fontFamily: T.body, fontSize: 13, color: INK, lineHeight: 1.45,
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {m.note}
          </div>
        )}

        {/* Toggle Public / Privé (colonne droite uniquement) */}
        {variant === 'for_card' && (
          <button
            onClick={e => { e.stopPropagation(); onTogglePublic && onTogglePublic(m) }}
            title={isPrivate ? 'Rendre public (page + carte)' : 'Rendre privé (email du joueur uniquement)'}
            style={{
              marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 9px', cursor: 'pointer',
              background: isPrivate ? 'transparent' : 'rgba(22,163,74,0.08)',
              border: `1px solid ${isPrivate ? RULE : 'rgba(22,163,74,0.3)'}`,
              color: isPrivate ? MUTED : GREEN,
              fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
            }}
          >
            {isPrivate ? <><Lock size={10} /> Privé</> : <><Globe size={10} /> Public</>}
          </button>
        )}
      </div>

      {/* Edit */}
      <button
        onClick={e => { e.stopPropagation(); onEdit && onEdit(m) }}
        title="Editer"
        style={{
          background: 'transparent', border: `1px solid ${RULE}`, color: MUTED,
          cursor: 'pointer', width: 28, height: 28, padding: 0, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = RULE; e.currentTarget.style.color = MUTED }}
      >
        <Edit3 size={12} />
      </button>

      {variant === 'private' && (
        <button
          onClick={e => { e.stopPropagation(); onMove && onMove(m) }}
          title="Marquer pour les joueurs"
          style={{
            flexShrink: 0, width: 28, height: 28, padding: 0, cursor: 'pointer',
            background: 'rgba(79,142,247,0.1)', border: `1px solid rgba(79,142,247,0.35)`,
            color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <ArrowRight size={13} />
        </button>
      )}
    </div>
  )
}

/**
 * Colonne de moments (Mes moments OU Pour les joueurs).
 *
 * @prop {Array}    moments
 * @prop {Array}    players          lineup pour resoudre les noms
 * @prop {function} onSeek           (timecode) => void
 * @prop {function} onEdit           (moment) => void
 * @prop {string}   variant          'private' | 'for_card'
 * @prop {string}   title            titre de la colonne
 * @prop {function} onMove           (moment) => void  (flèche dorée)
 * @prop {function} onTogglePublic   (moment) => void  (for_card uniquement)
 */
export default function MomentsList({
  moments = [], players = [], onSeek, onEdit,
  variant = 'private', title, onMove, onTogglePublic,
}) {
  const playerById = new Map(players.map(p => [p.player_id || p.id, p]))
  const heading = title || (variant === 'for_card' ? 'Pour les joueurs' : 'Mes moments')

  return (
    <div style={{ background: '#fff', border: `1px solid ${RULE}` }}>
      <div style={{
        padding: '12px 14px', borderBottom: `1px solid ${RULE}`,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ width: 14, height: 1, background: GOLD }} />
        <span style={{
          fontFamily: MONO, fontSize: 9, letterSpacing: '.18em',
          textTransform: 'uppercase', color: GOLD,
        }}>
          {heading} ({moments.length})
        </span>
      </div>

      {moments.length === 0 ? (
        <div style={{ padding: '28px 18px', textAlign: 'center' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, color: MUTED, margin: 0, lineHeight: 1.6, letterSpacing: '.04em' }}>
            {variant === 'for_card'
              ? 'Aucun moment pour les joueurs. Utilise la flèche dorée pour en ajouter ici.'
              : 'Aucun moment. Mets la vidéo en pause et clique sur "Marquer un moment".'}
          </p>
        </div>
      ) : (
        <div>
          {moments.map((m, idx) => (
            <MomentRow
              key={m.id}
              moment={m}
              last={idx === moments.length - 1}
              playerById={playerById}
              onSeek={onSeek}
              onEdit={onEdit}
              variant={variant}
              onMove={onMove}
              onTogglePublic={onTogglePublic}
            />
          ))}
        </div>
      )}
    </div>
  )
}
